import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	type ReactNode,
} from "react";
import { useLocation, useParams } from "@tanstack/react-router";
import { getActiveSection } from "@/layout/components/Sidebar";

export type WorkspaceSection =
	| "chats"
	| "docs"
	| "sheets"
	| "boards"
	| "meets"
	| "storage"
	| "passwords"
	| "automations"
	| "projects"
	| "more"
	| null;

interface WorkspaceSectionContextType {
	selectedSection: WorkspaceSection;
	setSelectedSection: (section: WorkspaceSection) => void;
}

const WorkspaceSectionContext = createContext<
	WorkspaceSectionContextType | undefined
>(undefined);

const STORAGE_KEY = "workspace-selected-section";

export function WorkspaceSectionProvider({
	children,
}: {
	children: ReactNode;
}) {
	const { pathname } = useLocation();
	const params = useParams({ from: "/$workspaceId" });
	const { workspaceId } = params;

	// Initialize from sessionStorage or default to Home
	const [selectedSection, setSelectedSectionState] = useState<WorkspaceSection>(
		() => {
			// Try sessionStorage first
			const stored = sessionStorage.getItem(`${STORAGE_KEY}-${workspaceId}`);
			if (stored === "home") return null;
			if (stored) return stored as WorkspaceSection;
			
			// Default to Home (null) if nothing stored
			return null;
		},
	);

	const setSelectedSection = (section: WorkspaceSection) => {
		setSelectedSectionState(section);
		// Persist in sessionStorage
		if (workspaceId) {
			if (section === null) {
				sessionStorage.setItem(`${STORAGE_KEY}-${workspaceId}`, "home");
			} else {
				sessionStorage.setItem(`${STORAGE_KEY}-${workspaceId}`, section);
			}
		}
	};

	useEffect(() => {
		if (!workspaceId) return;
		const stored = sessionStorage.getItem(`${STORAGE_KEY}-${workspaceId}`);
		if (stored === "home") {
			setSelectedSectionState(null);
			return;
		}
		if (stored) {
			setSelectedSectionState(stored as WorkspaceSection);
			return;
		}
		// Fallback: derive from current URL if nothing is stored for this workspace
		const derived = getActiveSection(pathname, workspaceId);
		setSelectedSectionState((derived as WorkspaceSection) ?? null);
	}, [workspaceId, pathname]);

	return (
		<WorkspaceSectionContext.Provider
			value={{
				selectedSection,
				setSelectedSection,
			}}
		>
			{children}
		</WorkspaceSectionContext.Provider>
	);
}

export function useWorkspaceSection() {
	const context = useContext(WorkspaceSectionContext);
	if (context === undefined) {
		throw new Error(
			"useWorkspaceSection must be used within a WorkspaceSectionProvider",
		);
	}
	return context;
}
